import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';

// import { InfoIcon } from '@/assets/Info';

import { SeetingIcon } from '@/assets/Setting';
import { LocationFooterIcon } from '@/assets/footer/Location';
import { RestaurantIcon } from '@/assets/Restaurant';
import { UserFooterIcon } from '@/assets/footer/User';

import { RowWithIcon } from '@/components/RowWithIcon';
import { Card } from '@/components/ui/card';
import { useTelegramBackButton } from '@/utils/BackButton';
import { userInfo } from '@/store/selectors/user';
import { formatPhoneNumber } from '@/utils/formatPhone';
import { Button } from '../../components/ui/button';
import { LoadingButton } from '../../components/ui/loadingButton';
import { AuthDrawer } from '../../components/AuthDrawer';
import { useState } from 'react';
import { CardIcon } from '../../assets/CardIcon';
import { ProfileBanners } from './ui/ProfileBanners';
import { clearUser } from '../../store/slices/userSlice';
import { resetPromo } from '../../store/slices/basketSlice';
import { clearLoyaltyUserId } from '../loyalty/libs/qrStorage';
import { removeAccountQueries } from '../auth/libs/session';
import { notificationOccurred, impactOccuer } from '../../utils/hapticEvents';
import { Drawer, DrawerContent, DrawerTitle } from '../../components/ui/drawer';

export const Profile = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const user = useSelector(userInfo);
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const [openLogout, setOpenLogout] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const queryClient = useQueryClient();
  const handleLogout = () => {
    setIsLoggingOut(true);
    // Brief delay so the spinner is actually visible before we navigate away —
    // otherwise the whole thing happens instantly and looks like nothing responded.
    setTimeout(() => {
      localStorage.removeItem('token');
      // Prevents AppProvider's Telegram-initData auto sign-in from silently logging
      // the user straight back in right after they explicitly logged out.
      localStorage.setItem('loggedOut', 'true');
      // Иначе офлайн-QR продолжил бы показывать id вышедшего пользователя.
      clearLoyaltyUserId();
      // История баллов, заказы, уведомления вышедшего аккаунта — из кэша вон,
      // иначе их увидит следующий, кто войдёт.
      removeAccountQueries(queryClient);
      // Промокод принадлежит сессии аккаунта — после выхода не подставляется.
      dispatch(resetPromo());
      dispatch(clearUser());
      setOpenLogout(false);
      setIsLoggingOut(false);
      notificationOccurred('success');
      navigate('/');
    }, 400);
  };

  useTelegramBackButton('/');

  return (
    <div className="px-4  flex flex-col items-center h-full  pt-4">
      <div className="flex flex-col items-center space-y-6 overflow-y-auto w-full">
        <div className="flex flex-col items-center space-y-3">
          {/* <div className="w-20 h-20 rounded-full bg-fill-base"> */}
          {/* <img
              src={'/assets/profile.png'}
              alt="cover"
              className="w-full h-full object-cover rounded-full"
            /> */}
          {/* </div> */}

          {user.isAuth && (
            <div className="flex flex-col items-center space-y-1">
              <p className="text-display-xl ">{user.name}</p>
              <p className="text-body-base-regular text-text-secondary">
                {formatPhoneNumber(user.phone)}
              </p>
            </div>
          )}
        </div>
        <div className="flex flex-col items-center space-y-4 w-full">
          {!user.isAuth && (
            <Card className="p-4 w-full flex flex-col space-y-4 rounded-2xl items-center justify-center ">
              <div className="flex flex-col items-center justify-center text-center">
                <p className="text-[18px] font-semibold font-inter-tight text-text-primary">
                  {t('auth.card.title')}
                </p>
                <p className="text-body-sm-regular text-text-tertiary">
                  {t('auth.card.text')}
                </p>
                <Button
                  className="mt-2 bg-button-primary rounded-xl w-full h-12"
                  onClick={() => setOpen(true)}
                >
                  {t('auth.card.button')}
                </Button>
              </div>
              <AuthDrawer open={open} setOpen={setOpen} />
            </Card>
          )}
          {/* {user.isAuth && (
            <div className="flex flex-row space-x-1 items-center">
              <EvosCoinCard />
              <ProfileWidgetCard
                title={t('promo')}
                route="/"
                icon={<PromoIcon className="text-text-icon-secondary" />}
              />
              <ProfileWidgetCard
                title={t('orders')}
                route="/profile/orders"
                icon={<OrderIcon className="text-text-icon-secondary" />}
              />
            </div>
          )} */}
          {/* Баннеры под именем (Figma 2488-99492) — только для аккаунта;
              при заполненных личных данных блока нет. */}
          {user.isAuth && <ProfileBanners />}
          {/* Сначала — всё про аккаунт, от самого личного к платёжному:
              данные, номер, адреса, карты. Отдельной карточкой, чтобы гостю
              (у него этого блока нет) не оставалось пустых промежутков. */}
          {user.isAuth && (
            <Card className="py-4 px-4 w-full flex flex-col space-y-4">
              <RowWithIcon
                navigateRoute="/profile/user-info"
                title={t('user-info')}
                icon={<UserFooterIcon className="text-text-icon-secondary " />}
              />
              <RowWithIcon
                navigateRoute="/profile/addresses"
                title={t('addresses')}
                icon={
                  <LocationFooterIcon className="text-text-icon-secondary h-5 " />
                }
              />
              <RowWithIcon
                navigateRoute="/profile/cards"
                title={t('card')}
                icon={<CardIcon className="text-text-icon-secondary" />}
              />
            </Card>
          )}

          {/* Общее, не привязанное к аккаунту; выход — последним пунктом. */}
          <Card className="py-4 px-4 w-full flex flex-col space-y-4">
            <RowWithIcon
              navigateRoute="/restaurants"
              title={t('restaurants')}
              icon={<RestaurantIcon className="text-text-icon-secondary h-5" />}
            />
            <RowWithIcon
              navigateRoute="/profile/settings"
              title={t('settings')}
              icon={<SeetingIcon className="text-text-icon-secondary" />}
            />
            {/* <RowWithIcon
              navigateRoute="/profile/about"
              title={t('about')}
              icon={<InfoIcon className="text-text-icon-secondary" />}
            /> */}
            {user.isAuth && (
              <div
                className="w-full flex flex-row space-x-3 items-center"
                onClick={() => {
                  impactOccuer('light');
                  setOpenLogout(true);
                }}
              >
                <div className="w-10 h-10 bg-background rounded-[10px] flex items-center justify-center">
                  <LogOut className="text-fill-danger h-5 w-5" />
                </div>
                <p className="text-body-base-regular font-inter text-fill-danger">
                  {t('logout')}
                </p>
              </div>
            )}
          </Card>
          {/* <Card className="p-4 w-full flex flex-col space-y-4 ">
            <RowWithIcon
              navigateRoute="/"
              title={t('help')}
              icon={<HelpIcon className="text-text-icon-secondary" />}
            />
          </Card> */}
          <Drawer open={openLogout} onOpenChange={setOpenLogout}>
            <DrawerContent className="bg-fill-background px-4">
              <div className="flex flex-col items-center pt-2 text-center">
                <DrawerTitle className="text-display-md max-w-[280px]">
                  {t('logout-confirm')}
                </DrawerTitle>
              </div>
              <p className="pt-1 text-body-sm-regular text-text-tertiary text-center">
                {t('logout-confirm-description')}
              </p>
              <div className="flex flex-row space-x-2 py-4">
                <LoadingButton
                  loading={isLoggingOut}
                  className="h-12 flex-1 rounded-xl bg-fill-danger shadow-none"
                  onClick={handleLogout}
                >
                  <p className="text-display-sm text-text-white">
                    {t('logout')}
                  </p>
                </LoadingButton>
                <Button
                  disabled={isLoggingOut}
                  className="h-12 flex-1 rounded-xl bg-button-secondary shadow-none"
                  onClick={() => setOpenLogout(false)}
                >
                  <p className="text-display-sm text-text-primary">
                    {t('cancel')}
                  </p>
                </Button>
              </div>
            </DrawerContent>
          </Drawer>
        </div>
      </div>
    </div>
  );
};
export default Profile;
